import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getSaleChannelName } from "./nhanhConfig";
import { convertExcelOrdersToTargetFormatNB } from "./nhanhExcelOrderConverterNB";
import { convertExcelOrdersToTargetFormatTC } from "./nhanhExcelOrderConverterTC";

// Format Unix timestamps for Excel export (Date only: DD/MM/YYYY)
const formatTimestamp = (ts) => {
  if (!ts) return "";
  const num = Number(ts);
  if (!Number.isNaN(num) && num > 0) {
    if (num < 100000000000) {
      return dayjs(num * 1000).format("DD/MM/YYYY");
    }
    return dayjs(num).format("DD/MM/YYYY");
  }
  if (
    typeof ts === "string" &&
    (ts.includes("-") || ts.includes("/") || ts.includes("T"))
  ) {
    const d = dayjs(ts);
    if (d.isValid()) return d.format("DD/MM/YYYY");
  }
  return String(ts);
};

// Format Unix timestamps for Excel export (Date only: YYMMDD)
const formatTimestampToYYMMDD = (ts) => {
  if (!ts) return "";
  const num = Number(ts);
  if (!Number.isNaN(num) && num > 0) {
    if (num < 100000000000) {
      return dayjs(num * 1000).format("YYMMDD");
    }
    return dayjs(num).format("YYMMDD");
  }
  if (
    typeof ts === "string" &&
    (ts.includes("-") || ts.includes("/") || ts.includes("T"))
  ) {
    const d = dayjs(ts);
    if (d.isValid()) return d.format("YYMMDD");
  }
  return String(ts);
};

// Format Expiry date (HSD) to DD/MM/YYYY
const formatHsd = (hsd) => {
  if (!hsd || hsd === "-") return "";
  const cleaned = String(hsd).trim();
  if (/^\d{8}$/.test(cleaned)) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/${cleaned.substring(4)}`;
  }
  if (/^\d{6}$/.test(cleaned)) {
    return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}/20${cleaned.substring(4)}`;
  }
  return cleaned;
};

// Extract detailed products information for Excel export and reallocate combo prices
const parseOrderProducts = (raw, productsCatalogMap) => {
  if (!raw) return [];
  const prods = raw.products || raw.items || raw.productList || [];
  let rawItems = [];
  if (Array.isArray(prods)) {
    rawItems = prods;
  } else if (prods && typeof prods === "object") {
    rawItems = Object.values(prods);
  }

  const rawGifts =
    raw.gifts || raw.gift || raw.giftItems || raw.info?.gifts || [];
  const orderGifts = Array.isArray(rawGifts)
    ? rawGifts
    : rawGifts && typeof rawGifts === "object"
      ? Object.values(rawGifts)
      : [];

  // Chỉ trả về dòng trống nếu cả sản phẩm và quà tặng đều rỗng
  if (rawItems.length === 0 && orderGifts.length === 0) {
    return [
      {
        name: "",
        sku: "",
        lot: "",
        hsd: "",
        imei: "",
        barcode: "",
        unit: "",
        category: "",
        weight: 0,
        originalPrice: 0,
        salePrice: 0,
        displaySalePrice: 0,
        priceDiff: 0,
        qty: 0,
        discountPerProduct: 0,
        totalDiscount: 0,
        priceAfterDiscount: 0,
      },
    ];
  }

  const parsedItems = [];
  const seenGiftKeys = new Set();
  const processGift = (gift) => {
    if (!gift) return;
    const giftName = String(gift.name || gift.productName || "").trim();
    const giftQty = Number(
      gift.quantity || gift.qty || gift.originalQuantity || 1,
    );
    const giftId = String(
      gift.productId || gift.id || gift.idNhanh || "",
    ).trim();
    const giftCode = String(
      gift.code || gift.productCode || gift.sku || "",
    ).trim();

    const key = `${giftId}_${giftName}_${giftQty}`;
    if (seenGiftKeys.has(key)) return;
    seenGiftKeys.add(key);

    const cleanGiftName = giftName
      .replace(/\s+(?:lô|lo)\s*[:.-]?\s*[A-Za-z0-9_-]+/i, "")
      .trim();

    // Look up from productsCatalogMap by gift name/id to get code if not available
    const giftLookupKeys = [
      giftId,
      giftCode,
      giftName,
      cleanGiftName,
      giftName.toLowerCase(),
      cleanGiftName.toLowerCase(),
      giftName.toUpperCase(),
      cleanGiftName.toUpperCase(),
      giftName.replace(/\s+/g, ""),
      cleanGiftName.replace(/\s+/g, ""),
      giftName.replace(/\s+/g, "").toLowerCase(),
      cleanGiftName.replace(/\s+/g, "").toLowerCase(),
    ].filter(Boolean);

    let giftSku = "";
    let giftVat = "";
    let giftLot = gift.lot || gift.batch || gift.batchNumber || "";
    let giftHsd = gift.hsd || gift.expiredDate || gift.expiryDate || "";
    let resolvedCode = giftCode;
    let catalogInfo = null;

    // Direct lookup by ID from productsCatalogMap
    if (giftId && productsCatalogMap) {
      if (productsCatalogMap[giftId]) {
        catalogInfo = productsCatalogMap[giftId];
      } else if (typeof productsCatalogMap === "object") {
        catalogInfo = Object.values(productsCatalogMap).find(
          (p) =>
            p &&
            String(p.id || p.productId || p.idNhanh || "") === String(giftId),
        );
      }
      if (catalogInfo) {
        if (
          !resolvedCode &&
          (catalogInfo.code || catalogInfo.sku || catalogInfo.productCode)
        ) {
          resolvedCode = String(
            catalogInfo.code || catalogInfo.sku || catalogInfo.productCode,
          ).trim();
        }
        if (
          catalogInfo.vat !== undefined &&
          catalogInfo.vat !== null &&
          catalogInfo.vat !== ""
        ) {
          giftVat = catalogInfo.vat;
        }
      }
    }

    for (const k of giftLookupKeys) {
      if (k && productsCatalogMap && productsCatalogMap[k]) {
        const info = productsCatalogMap[k];
        if (!resolvedCode && (info.code || info.sku || info.productCode)) {
          resolvedCode = String(
            info.code || info.sku || info.productCode,
          ).trim();
        }
        if (info.vat !== undefined && info.vat !== null && info.vat !== "") {
          giftVat = info.vat;
        }
        if (!catalogInfo && info) catalogInfo = info;
        if (resolvedCode) break;
      }
    }

    console.log("resolvedCode", resolvedCode);

    // Parse SKU/lot/HSD from code using same logic as Rule 2
    // Format: AV003-0030726-06072029
    const giftFormatRegex = /^[A-Za-z]{2}\d{3}-\w+-\d+$/;
    if (resolvedCode && giftFormatRegex.test(resolvedCode)) {
      const blocks = resolvedCode.split("-");
      const firstBlock = blocks[0].trim();
      giftSku =
        firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
      if (!giftLot) giftLot = blocks[1].trim();
      if (!giftHsd) giftHsd = blocks[2].trim();
    } else if (resolvedCode) {
      const blocks = resolvedCode.split("-");
      if (blocks.length >= 3 && blocks[0].trim().length >= 5) {
        const firstBlock = blocks[0].trim();
        giftSku =
          firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
        if (!giftLot) giftLot = blocks[1].trim();
        if (!giftHsd) giftHsd = blocks[2].trim();
      } else {
        giftSku = resolvedCode;
      }
    }

    // If giftLot is still empty, extract from giftName (e.g. "lô 0010526")
    if (!giftLot) {
      const lotMatch = giftName.match(
        /(?:lô|lo)\s*[:.-]?\s*([A-Za-z0-9_-]+)/i,
      );
      if (lotMatch) {
        giftLot = lotMatch[1].trim();
      }
    }

    // If giftHsd is still empty, extract from giftName
    if (!giftHsd) {
      const hsdMatch = giftName.match(
        /(?:hsd|exp|date)\s*[:.-]?\s*([0-9/.-]+)/i,
      );
      if (hsdMatch) {
        giftHsd = hsdMatch[1].trim();
      }
    }

    if (!giftSku) {
      giftSku = resolvedCode || giftId;
    }

    parsedItems.push({
      name: catalogInfo?.name || cleanGiftName || giftName,
      sku: giftSku,
      lot: giftLot,
      hsd: giftHsd,
      imei: gift.imei || "",
      barcode: gift.barcode || "",
      unit: gift.unit || "",
      category: gift.categoryName || gift.category || "",
      weight: Number(gift.weight || 0),
      originalPrice: 0,
      salePrice: 0,
      displaySalePrice: 0,
      priceDiff: 0,
      qty: giftQty,
      discountPerProduct: 0,
      totalDiscount: 0,
      priceAfterDiscount: 0,
      catalogUnitPrice: 0,
      vat: giftVat,
    });
  };

  rawItems.forEach((i) => {
    const rawName = String(i.productName || i.name || "").trim();
    const qty = Number(i.quantity || i.qty || 1);
    const price = Number(i.price || 0);
    const displaySalePrice = Number(
      i.displaySalePrice !== undefined && i.displaySalePrice !== null
        ? i.displaySalePrice
        : price,
    );
    const origPrice = Number(
      i.originalPrice || i.importPrice || i.listPrice || price,
    );
    const disc = Number(i.discountAmount ?? i.discount ?? 0);
    const w = Number(i.weight || 0);

    const itemCode = String(i.productCode || i.code || i.id || "").trim();
    const subItems = [];
    let isParsed = false;

    // Rule 1: Product code contains "CB" (case-insensitive)
    if (itemCode.toUpperCase().includes("CB")) {
      const parts = rawName.split(/\s+và\s+/i);
      if (parts.length > 0 && parts[0].includes("-")) {
        isParsed = true;
        parts.forEach((part) => {
          const trimmedPart = part.trim();
          if (!trimmedPart) return;

          const blocks = trimmedPart.split("-");
          if (blocks.length >= 3) {
            const firstBlock = blocks[0].trim();
            const pSku =
              firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
            const pLot = blocks[1].trim();
            let pHsd = blocks[2].trim();

            let pQty = 1;
            if (blocks.length >= 4) {
              const qtyMatch = blocks[3].match(/^(\d+)/);
              if (qtyMatch) {
                pQty = parseInt(qtyMatch[0], 10);
              } else {
                const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
                if (parenMatch) {
                  pQty = parseInt(parenMatch[1], 10);
                }
              }
            } else {
              const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
              if (parenMatch) {
                pQty = parseInt(parenMatch[1], 10);
              }
            }

            const finalSubQty = pQty * qty;

            // Look up in productsCatalogMap
            const cleanSku = pSku.trim();
            const cleanCode = trimmedPart
              .replace(/\s*\(.*?\)\s*$/, "")
              .replace(/-\d+$/, "");
            const lookupKeys = [
              cleanCode,
              cleanCode.toLowerCase(),
              cleanCode.toUpperCase(),
              cleanCode.replace(/\s+/g, ""),
              cleanCode.replace(/\s+/g, "").toLowerCase(),
              cleanSku,
              cleanSku.toLowerCase(),
              cleanSku.toUpperCase(),
              cleanSku.replace(/\s+/g, ""),
              cleanSku.replace(/\s+/g, "").toLowerCase(),
            ];
            let catalogUnitPrice = 0;
            let catalogVat = i.vat ?? i.taxRate ?? "-";
            let catalogName = "";
            for (const k of lookupKeys) {
              if (k && productsCatalogMap[k]) {
                const info = productsCatalogMap[k];
                if (info.originalPrice > 0 || info.price > 0) {
                  catalogUnitPrice =
                    info.originalPrice > 0 ? info.originalPrice : info.price;
                }
                if (info.vat !== undefined && info.vat !== null) {
                  catalogVat = info.vat;
                }
                if (info.name) {
                  catalogName = info.name;
                }
                if (catalogUnitPrice > 0 && catalogName) break;
              }
            }

            subItems.push({
              pName: catalogName || rawName,
              pSku: cleanSku,
              pLot,
              pHsd,
              pQty: finalSubQty,
              standaloneVal: catalogUnitPrice * finalSubQty,
              catalogUnitPrice,
              vat: catalogVat,
            });
          }
        });
      }
    }

    // Rule 2: Product code matches format like AV007-0010426-27042029
    const singleFormatRegex = /^[A-Za-z]{2}\d{3}-\w+-\d+$/;
    if (!isParsed && singleFormatRegex.test(itemCode)) {
      isParsed = true;
      const blocks = itemCode.split("-");
      const firstBlock = blocks[0].trim();
      const pSku =
        firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
      const pLot = blocks[1].trim();
      const pHsd = blocks[2].trim();
      const finalSubQty = qty;

      // Look up in productsCatalogMap
      const cleanSku = pSku.trim();
      const lookupKeys = [
        itemCode,
        itemCode.toLowerCase(),
        itemCode.toUpperCase(),
        itemCode.replace(/\s+/g, ""),
        itemCode.replace(/\s+/g, "").toLowerCase(),
        cleanSku,
        cleanSku.toLowerCase(),
        cleanSku.toUpperCase(),
        cleanSku.replace(/\s+/g, ""),
        cleanSku.replace(/\s+/g, "").toLowerCase(),
      ];

      let catalogUnitPrice = 0;
      let catalogVat = i.vat ?? i.taxRate ?? "-";
      let catalogName = "";
      for (const k of lookupKeys) {
        if (k && productsCatalogMap[k]) {
          const info = productsCatalogMap[k];
          if (info.originalPrice > 0 || info.price > 0) {
            catalogUnitPrice =
              info.originalPrice > 0 ? info.originalPrice : info.price;
          }
          if (info.vat !== undefined && info.vat !== null) {
            catalogVat = info.vat;
          }
          if (info.name) {
            catalogName = info.name;
          }
          if (catalogUnitPrice > 0 && catalogName) break;
        }
      }

      parsedItems.push({
        name: catalogName || rawName,
        sku: pSku,
        lot: pLot,
        hsd: pHsd,
        imei: i.imei || "",
        barcode: i.barcode || "",
        unit: i.unit || "",
        category: i.categoryName || i.category || "",
        weight: w,
        originalPrice: origPrice * finalSubQty,
        salePrice: price * finalSubQty,
        displaySalePrice: displaySalePrice * finalSubQty,
        priceDiff: (origPrice - price) * finalSubQty,
        qty: finalSubQty,
        discountPerProduct: finalSubQty > 0 ? disc / finalSubQty : 0,
        totalDiscount: disc,
        priceAfterDiscount: price * finalSubQty - disc,
        catalogUnitPrice,
        vat: catalogVat,
      });
    }

    // Default Fallback
    if (!isParsed) {
      const comboParts = rawName.split(/\s+và\s+/i);
      comboParts.forEach((part) => {
        const trimmedPart = part.trim();
        if (!trimmedPart) return;

        let pName = trimmedPart;
        let pSku = itemCode;
        let pLot = "";
        let pHsd = "";

        let pQty = 1;
        const parenMatch = trimmedPart.match(/\(\s*(\d+)/);
        if (parenMatch) {
          pQty = parseInt(parenMatch[1], 10);
        }

        const blocks = trimmedPart.split("-");
        if (blocks.length >= 3) {
          const firstBlock = blocks[0].trim();
          pSku = firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
          pLot = blocks[1].trim();
          pHsd = blocks[2].trim();
        }

        const finalSubQty = pQty * (comboParts.length > 1 ? qty : 1);

        // Get standalone catalog price from productsCatalogMap
        const cleanSku = String(pSku || "").trim();
        const cleanName = String(pName || "").trim();

        const lookupKeys = [
          cleanSku,
          cleanSku.toLowerCase(),
          cleanSku.toUpperCase(),
          cleanSku.replace(/\s+/g, ""),
          cleanSku.replace(/\s+/g, "").toLowerCase(),
          cleanName,
          cleanName.toLowerCase(),
          cleanName.toUpperCase(),
          cleanName.replace(/\s+/g, ""),
          cleanName.replace(/\s+/g, "").toLowerCase(),
        ];

        let catalogUnitPrice = 0;
        let catalogVat = i.vat ?? i.taxRate ?? "";
        for (const k of lookupKeys) {
          if (k && productsCatalogMap[k]) {
            const info = productsCatalogMap[k];
            if (info.originalPrice > 0 || info.price > 0) {
              catalogUnitPrice =
                info.originalPrice > 0 ? info.originalPrice : info.price;
            }
            if (info.vat !== undefined && info.vat !== null) {
              catalogVat = info.vat;
            }
            if (catalogUnitPrice > 0) break;
          }
        }

        const standaloneVal = catalogUnitPrice * finalSubQty;

        subItems.push({
          pName,
          pSku,
          pLot,
          pHsd,
          pQty: finalSubQty,
          standaloneVal,
          catalogUnitPrice,
          vat: catalogVat,
        });
      });
    }

    if (subItems.length > 0) {
      const totalComboSalePrice = price * qty;
      const totalComboDisplaySalePrice = displaySalePrice * qty;
      const totalComboOrigPrice = origPrice * qty;
      const totalComboDisc = disc;

      const sumStandaloneVal = subItems.reduce(
        (sum, sub) => sum + sub.standaloneVal,
        0,
      );

      subItems.forEach((sub) => {
        let ratio = 1 / subItems.length;
        if (sumStandaloneVal > 0) {
          ratio = sub.standaloneVal / sumStandaloneVal;
        }

        const itemSalePrice = Math.round(totalComboSalePrice * ratio);
        const itemDisplaySalePrice = Math.round(
          totalComboDisplaySalePrice * ratio,
        );
        const itemOrigPrice = Math.round(totalComboOrigPrice * ratio);
        const itemDisc = Math.round(totalComboDisc * ratio);
        const priceDiff = itemOrigPrice - itemSalePrice;
        const discountPerProduct = sub.pQty > 0 ? itemDisc / sub.pQty : 0;
        const priceAfterDiscount = itemSalePrice - itemDisc;

        parsedItems.push({
          name: sub.pName,
          sku: sub.pSku,
          lot: sub.pLot,
          hsd: sub.pHsd,
          imei: i.imei || "",
          barcode: i.barcode || "",
          unit: i.unit || "",
          category: i.categoryName || i.category || "",
          weight: w * sub.pQty,
          originalPrice: itemOrigPrice,
          salePrice: itemSalePrice,
          displaySalePrice: itemDisplaySalePrice,
          priceDiff,
          qty: sub.pQty,
          discountPerProduct,
          totalDiscount: itemDisc,
          priceAfterDiscount,
          catalogUnitPrice: sub.catalogUnitPrice,
          vat: sub.vat,
        });
      });
    }

    // Process gift items (quà tặng) — add as separate rows with all prices = 0
    const giftItems = Array.isArray(i.gifts) ? i.gifts : [];
    giftItems.forEach(processGift);
  });

  // Process order-level gift items (quà tặng cấp đơn hàng khi products rỗng hoặc kèm theo đơn)
  orderGifts.forEach(processGift);

  if (parsedItems.length === 0) {
    return [
      {
        name: "",
        sku: "",
        lot: "",
        hsd: "",
        imei: "",
        barcode: "",
        unit: "",
        category: "",
        weight: 0,
        originalPrice: 0,
        salePrice: 0,
        displaySalePrice: 0,
        priceDiff: 0,
        qty: 0,
        discountPerProduct: 0,
        totalDiscount: 0,
        priceAfterDiscount: 0,
      },
    ];
  }

  return parsedItems;
};

// Handle Export Excel with exact 88 specified columns
export const exportOrdersToExcel = ({
  orders,
  selectedKeys,
  productsCatalogMap,
  depotsMap,
  invoiceDataMap = {},
  triggerAlert,
}) => {
  if (selectedKeys.size === 0) {
    triggerAlert(
      "Vui lòng chọn ít nhất một đơn hàng để xuất Excel!",
      "warning",
    );
    return;
  }

  const targetOrders = orders.filter((o) => selectedKeys.has(o.id));

  if (targetOrders.length === 0) {
    triggerAlert("Không tìm thấy dữ liệu đơn hàng đã chọn!", "warning");
    return;
  }

  const baseHeaders = [
    "Ngày Xuất Hàng",
    "Loại chứng từ",
    "Số chứng từ",
    "Số Đơn Hàng",
    "Ngày đơn hàng",
    "Ông bà",
    "Địa chỉ",
    "Ghi chú",
    "ĐVTT",
    "Tỷ giá HT",
    "Tên Khách Hàng",
    "[Đối tượng công nợ] Tên KHNB",
    "Mã KH",
    "Đối tượng ghi có",
    "[Đối tượng ghi có] Tên KHNB",
    "[Đối tượng ghi có] Mã",
    "Chi tiết các khoản",
    "TK nợ",
    "TK có",
    "Tên Sản Phẩm",
    "[Vật tư hàng hóa] Đvt",
    "Mã Sản Phẩm",
    "Đơn Vị Lẻ",
    "Kho hàng",
    "Kho nhận",
    "Số Lot",
    "Ngày Hết Hạn",
    "Số lượng Lẻ",
    "Hesoquydoi",
    "Số lượng",
    "Đơn giá VND",
    "Tiền Ngtệ",
    "Doanh Số",
    "Thuế suất",
    "TK nợ VAT",
    "Tk có VAT",
    "Thành Tiền Thuế",
    "% Chiết Khấu",
    "TK Nợ CK",
    "TK Có CK",
    "Tổng Tiền CK",
    "Mẫu H.đơn",
    "Seri",
    "Ngày hóa đơn",
    "Số hóa đơn",
    "Nhóm VAT",
    "MS thuế",
    "KH thuế",
    "ĐC thuế",
    "Hình Thức Thanh Toán",
    "Bộ phận",
    "GVTC",
    "TK GVHB",
    "TK hạch toán",
    "Giá vốn",
    "TSCĐ",
    "[TSCĐ] Mã",
    "Tên Kênh KH",
    "Chương trình",
    "[Chương trình] Mã",
    "Khoản mục Nội bộ",
    "[Khoản mục Nội bộ] Mã",
    "Hop dong",
    "Số phiếu MTV",
    "Vùng",
    "Đơn giá (đvt)",
    "Dòng tiền",
    "[Dòng tiền] Mã",
    "Khoản mục Thuế",
    "Tên Nhân Viên",
    "Mảng kinh doanh",
    "Mã NV (10 ký tự)",
    "Ngân hàng",
    "Kèm theo . . . . . .",
    "Số tiền 2 Ngtệ",
    "Kênh KH",
    "Mức CK T.Toán",
    "Phân xưởng",
    "[Phân xưởng] Mã",
    "ĐTCP_TP",
    "[ĐTCP_TP] Mã",
    "Số lô SP",
    "SLSX",
    "Mã Kho",
    "[Kho nhận] Mã",
    "[Mảng kinh doanh] Mã",
    "Ngày hạn t.t",
  ];

  const headersNB = [
    ...baseHeaders,
    "Đơn giá sau CK",
    "Thành tiền sau CK",
  ];

  const buildSheetData = (isTC, isHTLNB = false) => {
    const isImportNB = !isTC && !isHTLNB;
    const hasDiscountCols = !isTC; // NB and HTLNB both get extra columns
    const currentHeaders = hasDiscountCols ? headersNB : baseHeaders;

    const returnStatusIds = [71, 72, 74, "71", "72", "74"];
    const filteredOrders = targetOrders.filter((o) => {
      const isReturn = returnStatusIds.includes(o.status);
      return isHTLNB ? isReturn : !isReturn;
    });

    return [
      ["BẢNG KÊ CHI TIẾT ĐƠN HÀNG TMDT NHANH.VN"],
      [`Ngày xuất file: ${dayjs().format("DD/MM/YYYY")}`],
      [],
      currentHeaders,
      ...filteredOrders.flatMap((o) => {
        const raw = o.raw || {};
        const info = raw.info || {};
        const channel = raw.channel || {};
        const carrier = raw.carrier || {};
        const prodItems = parseOrderProducts(raw, productsCatalogMap);

        const orderIdStr = info.id || raw.id || o.id || "";
        const shopOrderIdStr =
          channel.appOrderId ||
          raw.appOrderId ||
          o.shopOrderId ||
          o.appOrderId ||
          orderIdStr;
        const createdDateStr = formatTimestamp(info.createdAt || raw.createdAt);
        const dateYYMMDD = formatTimestampToYYMMDD(
          info.createdAt || raw.createdAt,
        );
        const trackingCodeStr =
          carrier.carrierCode ||
          carrier.code ||
          raw.trackingCode ||
          carrier.trackingCode ||
          info.carrierCode ||
          raw.carrierCode ||
          "";
        const soChungTu =
          dateYYMMDD && trackingCodeStr
            ? `${dateYYMMDD}-${trackingCodeStr}`
            : "";

        const invoiceInfo = invoiceDataMap[o.id] || {};
        const ngayHoaDon = o.invoiceDate || invoiceInfo.invoiceDate || "";
        const soHoaDon = o.invoiceNumber || invoiceInfo.invoiceNumber || "";
        const ngayXuatHang = ngayHoaDon ? ngayHoaDon : "";

        const channelStr = String(
          getSaleChannelName(channel.saleChannel ?? raw.saleChannel) || "",
        ).toLowerCase();
        let maKH = "AVMKH033251";
        let maLoaiDiemBan = "LDB_1901";
        let kenhKH = "KBH_012";
        if (
          channelStr.includes("shoppe") ||
          channelStr.includes("shopee") ||
          channel.saleChannel === 42 ||
          raw.saleChannel === 42
        ) {
          maKH = "AVMKH033249";
          maLoaiDiemBan = "LDB_2001";
          kenhKH = "KBH_012";
        } else if (
          channelStr.includes("tiktok") ||
          channel.saleChannel === 48 ||
          raw.saleChannel === 48
        ) {
          maKH = "AVMKH033251";
          maLoaiDiemBan = "LDB_1901";
          kenhKH = "KBH_014";
        }

        const costVal = Number(raw.costPrice ?? raw.capitalPrice ?? 0);

        return prodItems.map((prod) => {
          // Map SKU exported in Excel back to productsCatalogMap to fetch catalog original price & vat
          const cleanSku = String(prod.sku || "").trim();
          const cleanName = String(prod.name || "").trim();
          const lookupKeys = [
            cleanSku,
            cleanSku.toLowerCase(),
            cleanSku.toUpperCase(),
            cleanSku.replace(/\s+/g, ""),
            cleanSku.replace(/\s+/g, "").toLowerCase(),
            cleanName,
            cleanName.toLowerCase(),
            cleanName.toUpperCase(),
            cleanName.replace(/\s+/g, ""),
            cleanName.replace(/\s+/g, "").toLowerCase(),
          ];

          let catalogUnitPrice = 0;
          let catalogVat = prod.vat !== undefined ? prod.vat : "";
          for (const k of lookupKeys) {
            if (k && productsCatalogMap[k]) {
              const infoItem = productsCatalogMap[k];
              if (infoItem.originalPrice > 0 || infoItem.price > 0) {
                catalogUnitPrice =
                  infoItem.originalPrice > 0
                    ? infoItem.originalPrice
                    : infoItem.price;
              }
              if (infoItem.vat !== undefined && infoItem.vat !== null) {
                catalogVat = infoItem.vat;
              }
              if (catalogUnitPrice > 0) break;
            }
          }
          const baseSalePrice =
            prod.displaySalePrice !== undefined &&
              prod.displaySalePrice !== null
              ? prod.displaySalePrice
              : prod.salePrice || 0;

          const vatNum = Number(catalogVat);
          const isVatValid = !Number.isNaN(vatNum) && vatNum > 0;

          const priceBeforeVat = isVatValid
            ? baseSalePrice / (1 + vatNum / 100)
            : baseSalePrice;

          const discountBeforeVat = isVatValid
            ? prod.totalDiscount / (1 + vatNum / 100)
            : prod.totalDiscount;

          const roundedDiscountBeforeVat = Math.round(discountBeforeVat);
          const tkNoCK = isTC ? "" : prod.totalDiscount > 0 ? "1311" : "";
          const tkCoCK = isTC ? "" : prod.totalDiscount > 0 ? "64111" : "";
          const totalDiscountVal = isTC ? "" : roundedDiscountBeforeVat;

          const salePriceValTC = Math.round(priceBeforeVat - discountBeforeVat);
          const salePriceVal = isTC
            ? salePriceValTC
            : Math.round(priceBeforeVat);

          const unitPriceBeforeVat =
            prod.qty > 0
              ? Math.round(priceBeforeVat / prod.qty)
              : Math.round(priceBeforeVat);

          const khoanMucNoiBoMa =
            prod.totalDiscount > 0 ||
              baseSalePrice === 0 ||
              unitPriceBeforeVat === 0
              ? "KM0101"
              : "";

          const thanhTienThue = isVatValid
            ? Math.round((salePriceValTC * vatNum) / 100)
            : 0;

          const isHKM = baseSalePrice === 0 || unitPriceBeforeVat === 0;

          return [
            ngayXuatHang, // 1. Ngày Xuất Hàng
            "", // 2. Loại chứng từ
            shopOrderIdStr, // 3. Số chứng từ = Số Đơn Hàng
            shopOrderIdStr, // 4. Số Đơn Hàng
            createdDateStr, // 5. Ngày đơn hàng
            "", // 6. Ông bà
            "", // 7. Địa chỉ
            trackingCodeStr, // 8. Ghi chú
            "VNĐ", // 9. ĐVTT
            "", // 10. Tỷ giá HT
            "", // 11. Tên Khách Hàng
            "", // 12. [Đối tượng công nợ] Tên KHNB
            maKH, // 13. Mã KH
            "", // 14. Đối tượng ghi có
            "", // 15. [Đối tượng ghi có] Tên KHNB
            "", // 16. [Đối tượng ghi có] Mã
            "", // 17. Chi tiết các khoản
            isTC
              ? (isHTLNB ? "52121" : "1311")
              : (isHTLNB
                ? (isHKM ? "155" : "52121")
                : (isHKM ? "64111" : "1311")), // 18. TK nợ
            isTC
              ? (isHTLNB ? "1311" : "51121")
              : (isHTLNB
                ? (isHKM ? "64111" : "1311")
                : (isHKM ? "155" : "51121")), // 19. TK có
            "", // 20. Tên Sản Phẩm
            "", // 21. [Vật tư hàng hóa] Đvt
            prod.sku, // 22. Mã Sản Phẩm
            "", // 23. Đơn Vị Lẻ
            "", // 24. Kho hàng
            "", // 25. Kho nhận
            prod.lot, // 26. Số Lot
            formatHsd(prod.hsd), // 27. Ngày Hết Hạn
            prod.qty, // 28. Số lượng Lẻ
            "", // 29. Hesoquydoi
            prod.qty, // 30. Số lượng
            unitPriceBeforeVat, // 31. Đơn giá VND
            "", // 32. Tiền Ngtệ
            salePriceVal, // 33. Doanh Số
            catalogVat !== "" && catalogVat !== undefined && catalogVat !== null
              ? catalogVat
              : "", // 34. Thuế suất
            isHTLNB ? "333111" : isHKM ? "" : "1311", // 35. TK nợ VAT
            isHTLNB ? "1311" : isHKM ? "" : "333111", // 36. Tk có VAT
            thanhTienThue, // 37. Thành Tiền Thuế
            "", // 38. % Chiết Khấu
            tkNoCK, // 39. TK Nợ CK
            tkCoCK, // 40. TK Có CK
            totalDiscountVal, // 41. Tổng Tiền CK

            "", // 42. Mẫu H.đơn
            "", // 43. Seri
            ngayHoaDon, // 44. Ngày hóa đơn
            soHoaDon, // 45. Số hóa đơn
            "", // 46. Nhóm VAT
            "", // 47. MS thuế
            "", // 48. KH thuế
            "", // 49. ĐC thuế
            "", // 50. Hình Thức Thanh Toán
            "", // 51. Bộ phận
            "", // 52. GVTC
            isTC
              ? (isHTLNB ? "155" : "6321")
              : (isHTLNB
                ? (isHKM ? "" : "155")
                : (isHKM ? "" : "6321")), // 53. TK GVHB
            isTC
              ? (isHTLNB ? "6321" : "155")
              : (isHTLNB
                ? (isHKM ? "" : "6321")
                : (isHKM ? "" : "155")), // 54. TK hạch toán
            costVal, // 55. Giá vốn
            "", // 56. TSCĐ
            "", // 57. [TSCĐ] Mã
            "", // 58. Tên Kênh KH
            "", // 59. Chương trình
            "", // 60. [Chương trình] Mã
            "", // 61. Khoản mục Nội bộ
            khoanMucNoiBoMa, // 62. [Khoản mục Nội bộ] Mã
            "", // 63. Hop dong
            "", // 64. Số phiếu MTV
            "", // 65. Vùng
            "", // 66. Đơn giá (đvt)
            "", // 67. Dòng tiền
            "", // 68. [Dòng tiền] Mã
            "", // 69. Khoản mục Thuế
            "", // 70. Tên Nhân Viên
            "", // 71. Mảng kinh doanh
            "", // 72. Mã NV (10 ký tự)
            "", // 73. Ngân hàng
            "", // 74. Kèm theo . . . . . .
            "", // 75. Số tiền 2 Ngtệ
            kenhKH, // 76. Kênh KH
            "", // 77. Mức CK T.Toán
            "", // 78. Phân xưởng
            "", // 79. [Phân xưởng] Mã
            "", // 80. ĐTCP_TP
            "", // 81. [ĐTCP_TP] Mã
            "", // 82. Số lô SP
            "", // 83. SLSX
            "AVMKK1407", // 84. Mã Kho
            "", // 85. [Kho nhận] Mã
            "AVMMKD0004", // 86. [Mảng kinh doanh] Mã
            "", // 87. Ngày hạn t.t
            ...(hasDiscountCols
              ? [
                unitPriceBeforeVat, // 88. Đơn giá sau CK
                salePriceValTC, // 89. Thành tiền sau CK
              ]
              : []),
          ];
        });
      }),
    ];
  };

  const wb = XLSX.utils.book_new();

  const dataNB = buildSheetData(false, false);
  const dataTC = buildSheetData(true, false);
  const dataHTLNB = buildSheetData(false, true);
  const dataHTLTC = buildSheetData(true, true);

  const wsNB = XLSX.utils.aoa_to_sheet(dataNB);
  XLSX.utils.book_append_sheet(wb, wsNB, "Import NB");

  const wsTC = XLSX.utils.aoa_to_sheet(dataTC);
  // XLSX.utils.book_append_sheet(wb, wsTC, "Import TC");

  const wsHTLNB = XLSX.utils.aoa_to_sheet(dataHTLNB);
  XLSX.utils.book_append_sheet(wb, wsHTLNB, "HTLNB");

  const wsHTLTC = XLSX.utils.aoa_to_sheet(dataHTLTC);
  // XLSX.utils.book_append_sheet(wb, wsHTLTC, "HTLTC");

  // Apply column widths and header merges
  const applyStyles = (ws, data, currentHeaders) => {
    const maxColWidths = currentHeaders.map((_, colIdx) => {
      const maxLen = data.reduce((max, row) => {
        const cell = row[colIdx];
        const len = cell ? cell.toString().length : 0;
        return Math.max(max, len);
      }, 0);
      return { wch: Math.min(Math.max(maxLen + 3, 10), 40) };
    });
    ws["!cols"] = maxColWidths;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: currentHeaders.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: currentHeaders.length - 1 } },
    ];
  };

  applyStyles(wsNB, dataNB, headersNB);
  applyStyles(wsTC, dataTC, baseHeaders);
  applyStyles(wsHTLNB, dataHTLNB, headersNB);
  applyStyles(wsHTLTC, dataHTLTC, baseHeaders);

  // --- Chạy converter cho file export ---
  const mapAoaToObjects = (aoa, orderType) => {
    const headersList = aoa[3];
    const rows = aoa.slice(4);
    return rows.map(r => {
      const obj = { orderType };
      headersList.forEach((h, i) => {
        obj[h] = r[i];
      });
      return obj;
    });
  };

  const objectsNB = mapAoaToObjects(dataNB, "Bán");
  const objectsHTLNB = mapAoaToObjects(dataHTLNB, "Trả");

  const combinedNB = [...objectsNB, ...objectsHTLNB];
  if (combinedNB.length > 0) {
    const convertedNB = convertExcelOrdersToTargetFormatNB(combinedNB);
    console.log("Dữ liệu Đơn Hàng xuất Excel NB sau khi convert:", convertedNB);
  }

  const objectsTC = mapAoaToObjects(dataTC, "Bán");
  const objectsHTLTC = mapAoaToObjects(dataHTLTC, "Trả");

  const combinedTC = [...objectsTC, ...objectsHTLTC];
  if (combinedTC.length > 0) {
    const convertedTC = convertExcelOrdersToTargetFormatTC(combinedTC);
    console.log("Dữ liệu Đơn Hàng xuất Excel TC sau khi convert:", convertedTC);
  }
  // ------------------------------------

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `DonHang_Nhanh_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
  triggerAlert(
    `Đã xuất Excel thành công ${targetOrders.length} đơn hàng với 2 sheet Import NB và HTLNB!`,
    // `Đã xuất Excel thành công ${targetOrders.length} đơn hàng với 4 sheet Import NB, Import TC,  HTLNB và HTLTC!`,
  );
};
